
import { Component, Inject, Input, OnInit, OnDestroy } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';
import { Observable, Subscription, take } from 'rxjs';

import { FilterService } from '../../services/abstract-filter.service';
import { TargetingListList } from '../../../../Discover/Targeting/models/targeting-list-list.model';
import { DistributionStatus } from '../../../TargetingShared/enum/distribution-status.enum';
import { DistributionSummaryModel } from '../../../TargetingShared/model/distribution-summary-model';
import { SourcePickerInterface } from '../../../TargetingShared/services/source-picker.interface';
import { DropdownOptionModel } from '../../../Forms/models/dropdown-option.model';
import { DiscoverListTypeEnum } from '../../../../api/mentions/enum/discover-list-type.enum';
import { TargetingSearchList } from '../../../../Discover/Targeting/models/targeting-search-list.model';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';

import { AbstractFilterTypeComponent } from '../../models/abstract-filter-type.component';
import { DiscoverListsFilterModel } from '../../models/discover-lists-filter.model';
import { DiscoverListFilterModel } from '../../../../api/mentions/model/discover-list-filter.model';
import { IncludeExcludeEnum } from '../../enums/include-exclude.enum';
import { DiscoverListsFilterService } from '../../services/discover-lists-filter.service';
import { AuditableModel } from '../../../Misc/models/auditable.model';
import { DistributionSummaryApiModel } from '../../../../api/distributions/model/distribution-summary-api.model';

@Component({
    selector: 'agpr-discover-lists-filter',
    templateUrl: './discover-lists-filter.component.html',
    styleUrls: ['./discover-lists-filter.component.scss'],
    providers: [
        {
            provide: FilterService,
            useExisting: 'SourcePickerService'
        },
        {
            provide: AbstractFilterTypeComponent,
            useExisting: DiscoverListsFilterComponent
        }
    ],
    standalone: false
})

export class DiscoverListsFilterComponent extends AbstractFilterTypeComponent implements OnInit, OnDestroy {

    @Input() id: string = 'discoverListFilter';
    @Input() declare filter: DiscoverListsFilterModel;

    readonly discoverListTypes = DiscoverListTypeEnum;
    readonly buttonTypes = ButtonTypesEnum;

    subscriptions: Subscription[] = [];
    sourceSubscription: Subscription;

    value: DiscoverListFilterModel;

    discoverListTypeOptions: DropdownOptionModel[] = [
        new DropdownOptionModel('Monitor.none', DiscoverListTypeEnum.none),
        new DropdownOptionModel(`Discover.listSource.${DiscoverListTypeEnum.targetingList}`, DiscoverListTypeEnum.targetingList),
        new DropdownOptionModel(`Discover.listSource.${DiscoverListTypeEnum.targetingSearch}`, DiscoverListTypeEnum.targetingSearch),
        new DropdownOptionModel(`Discover.listSource.${DiscoverListTypeEnum.distribution}`, DiscoverListTypeEnum.distribution),
    ];

    selectedListType: DiscoverListTypeEnum = DiscoverListTypeEnum.none;

    source$: Observable<AuditableModel[]>;
    selectedList: AuditableModel;

    viewSourceTooltip: string;
    searchAssociatedOutlets: boolean = false;

    constructor(
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        @Inject('SourcePickerService') public sourcePickerService: SourcePickerInterface,
        public discoverListsFilterService: DiscoverListsFilterService
    ) {
        super();
    }

    ngOnInit(){
        this.refresh();
        this.subscriptions.push(
            this.sourcePickerService.getItemCount().subscribe(
                total => {
                    if (total === null) {
                        return;
                    }
                    if (this.value) {
                        this.value.contactCount = total;
                        this.update();
                    }
                }
            )
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    currentIncludeExcludeChange() {
        if(this.value) {
            if(this.filter?.currentIncludeExclude === IncludeExcludeEnum.include) {
                const list = this.value?.discoverList?.exclude || [];
                this.value.discoverList = {
                    include: [...list],
                    exclude: []
                };
            }
            if(this.filter?.currentIncludeExclude === IncludeExcludeEnum.exclude) {
                const list = this.value?.discoverList?.include || [];
                this.value.discoverList = {
                    include: [],
                    exclude: [...list]
                };
            }
        }
        this.update();
    }

    selectListType() {
        const newValue = new DiscoverListFilterModel();
        newValue.discoverListType = this.selectedListType;
        newValue.useEmploymentOutletId = false;
        this.value = newValue;

        this.selectedList = null;
        this.setAutocompleteSuggestions();
        this.viewSourceTooltip = `Discover.listPicker.viewSource.${this.selectedListType}`;

        this.update();
    }

    setAutocompleteSuggestions() {
        this.source$ = this.discoverListsFilterService.getDiscoverList(this.selectedListType);
        if(this.sourceSubscription) {
            this.sourceSubscription.unsubscribe();
        }
        this.sourceSubscription = this.source$.subscribe(source => {
            if(source) {
                this.filter.source = source;
                this.selectedList = source.find(item => item.id === this.value.discoverList[this.filter.currentIncludeExclude][0]);
                this.selectSource(this.selectedList);
            }
        });
    }

    selectSource(source: AuditableModel) {
        if (this.value) {
            if (source?.id) {
                this.value.discoverList = this.filter.currentIncludeExclude === IncludeExcludeEnum.exclude ? {
                    include: [],
                    exclude: [source.id]
                } : {
                    include: [source.id],
                    exclude: []
                };
            } else {
                this.value.discoverList = {
                    include: [],
                    exclude: []
                };
                this.value.contactCount = null;
            }
        }

        if (source instanceof TargetingListList) {
            this.sourcePickerService.setActiveList(source, false);
        } else if (source instanceof TargetingSearchList) {
            this.sourcePickerService.setActiveSearch(source, false);
        } else if (source instanceof DistributionSummaryModel) {
            this.sourcePickerService.setActiveDistribution(source, false);
        }

        this.update();
    }

    setDistributionRecipientSearchMode() {
        this.value.useEmploymentOutletId = this.searchAssociatedOutlets;
        this.update();
    }

    viewSource() {
        switch (this.selectedListType) {
            case DiscoverListTypeEnum.targetingList:
                window.open(this.appBaseHref + `Discover/targetinglists/list/${this.selectedList?.id}/results`, '_blank');
                break;
            case DiscoverListTypeEnum.targetingSearch:
                window.open(this.appBaseHref + `Discover/targetingsearches/search/${this.selectedList?.id}/results`, '_blank');
                break;
            case DiscoverListTypeEnum.distribution:
                if ((this.selectedList as DistributionSummaryApiModel).status === DistributionStatus.draft) {
                    window.open(this.appBaseHref + `Discover/distributions/distribution/${this.selectedList?.id}?step=editRecipients`, '_blank');
                } else {
                    window.open(this.appBaseHref + `Discover/distributions/distribution/${this.selectedList?.id}/report`, '_blank');
                }
                break;
        }
    }

    update() {
        this.filter.setCurrentValue(this.value);
    }

    refresh() {
        this.value = this.filter.getCurrentValue();
        const selectedListType = this.discoverListTypeOptions.find(item => item.value === this.value?.discoverListType);
        this.selectedListType = selectedListType ? selectedListType.value : DiscoverListTypeEnum.none;
        this.viewSourceTooltip = `Discover.listPicker.viewSource.${this.selectedListType}`;
        this.searchAssociatedOutlets = !!this.value?.useEmploymentOutletId;
        this.setAutocompleteSuggestions();
    }

}
